import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Flex, Text, Table, Button } from "@radix-ui/themes";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { Version } from "@/v2/entities/release/Release.model";
import { useReleaseStats } from "@/v2/features/simulator/context/useReleaseStats";
import styles from "./styles.module.scss";
import { VersionsNavBar } from "./versions-nav-bar";
import { ReadOnlyMarkdown } from "@/v2/components/editor/ReadOnlyMarkdown";

interface VersionInformationPageProps {
  version: Version;
}

const buildActivityUrl = (
  simulatorId: number,
  versionId: number,
  topicName?: string
) => {
  const searchParams = new URLSearchParams();
  searchParams.set("selectedReleaseId", versionId.toString());
  searchParams.set("sortOrder", "newest");

  if (topicName) {
    const topicFilter = JSON.stringify([{ type: "topic", value: topicName }]);
    searchParams.set("selectedFilters", topicFilter);
  }

  return `/automation/${simulatorId}/activity?${searchParams.toString()}`;
};

export const VersionInformationPage: React.FC<VersionInformationPageProps> = ({
  version,
}: VersionInformationPageProps) => {
  const [expandedTopics, setExpandedTopics] = useState<string[]>([]);
  const navigate = useNavigate();

  const releaseStats = useReleaseStats(version.id.toString());

  const toggleTopic = (topicName: string) => {
    setExpandedTopics((prev) =>
      prev.includes(topicName)
        ? prev.filter((t) => t !== topicName)
        : [...prev, topicName]
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <VersionsNavBar versionIndex={version.version} />
      <Flex direction={"column"} gap={"8"} px={"8"} py={"8"}>
        <div className={styles.header}>
          <Flex justify="between" align="center" width="100%">
            <Flex direction="column" gap="2">
              <Flex align="center" gap="2">
                <Text size="6" weight="bold">
                  Version {version.version}
                </Text>
                {version.status === "published" && (
                  <Badge size="2" color="green">
                    Live Version
                  </Badge>
                )}
                {version.status === "draft" && !version.publishedAt && (
                  <Badge size="2" color="gray">
                    Currently in training
                  </Badge>
                )}
              </Flex>
              <Text size="2" color="gray">
                {version.publishedAt
                  ? `Published on ${new Date(
                      version.publishedAt
                    ).toLocaleDateString()}`
                  : "Not published yet"}
              </Text>
            </Flex>
            <Button
              color="gray"
              variant="outline"
              size="2"
              onClick={() =>
                navigate(buildActivityUrl(version.simulatorId, version.id))
              }
            >
              See All Activity
            </Button>
          </Flex>
        </div>

        <div className={styles.content}>
          <div className={styles.section}>
            <Flex gap="8">
              <div className={styles.stat}>
                <Text size="6" weight="bold">
                  {releaseStats.topicsCount}
                </Text>
                <Text size="2" color="gray" className={styles.statLabel}>
                  Topics
                </Text>
              </div>
              <div className={styles.stat}>
                <Text size="6" weight="bold">
                  {releaseStats.instructions.length}
                </Text>
                <Text size="2" color="gray" className={styles.statLabel}>
                  Instructions
                </Text>
              </div>
              <div className={styles.stat}>
                <Text size="6" weight="bold">
                  {releaseStats.conversationsCount}
                </Text>
                <Text size="2" color="gray" className={styles.statLabel}>
                  Tested Sessions
                </Text>
              </div>
              <div className={styles.stat}>
                <Text size="6" weight="bold" className="text-green-600">
                  {releaseStats.goodFeedbackCount}
                </Text>
                <Flex align="center" gap="2" className={styles.statLabel}>
                  <ThumbsUp className="w-4 h-4" />
                  <Text size="2" color="gray">
                    Good Feedback
                  </Text>
                </Flex>
              </div>
              <div className={styles.stat}>
                <Text size="6" weight="bold" className="text-red-600">
                  {releaseStats.badFeedbackCount}
                </Text>
                <Flex align="center" gap="2" className={styles.statLabel}>
                  <ThumbsDown className="w-4 h-4" />
                  <Text size="2" color="gray">
                    Bad Feedback
                  </Text>
                </Flex>
              </div>
            </Flex>
          </div>

          <div className={styles.section}>
            <Text size="4" weight="bold" mb="4">
              Training Data
            </Text>
            <Table.Root>
              <Table.Header>
                <Table.Row className="text-gray-500 font-normal">
                  <Table.ColumnHeaderCell className="font-normal">
                    Topic
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="font-normal">
                    Instructions
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="font-normal">
                    Sessions
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="font-normal">
                    <Flex align="center" gap="2">
                      Good Feedback
                      <ThumbsUp className="w-4 h-4" />
                    </Flex>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="font-normal">
                    <Flex align="center" gap="2">
                      Bad Feedback
                      <ThumbsDown className="w-4 h-4" />
                    </Flex>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="font-normal">
                    Brief
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {releaseStats.topics.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={6}>
                      <Flex direction="column" align="center" gap="2" py="6">
                        <Text size="2" weight="medium">
                          No topics available
                        </Text>
                        <Text size="2" color="gray">
                          This version doesn't have any topics.
                        </Text>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  releaseStats.topics.map((topic) => {
                    return (
                      <React.Fragment key={topic.topicName}>
                        <Table.Row
                          className={`cursor-pointer hover:bg-gray-100 ${
                            expandedTopics.includes(topic.topicName)
                              ? styles.expanded
                              : ""
                          }`}
                          onClick={() => toggleTopic(topic.topicName)}
                        >
                          <Table.Cell>
                            <Flex align="center" gap="2">
                              {expandedTopics.includes(topic.topicName) ? (
                                <ChevronDownIcon />
                              ) : (
                                <ChevronRightIcon />
                              )}
                              <Text>{topic.topicName}</Text>
                            </Flex>
                          </Table.Cell>
                          <Table.Cell>{topic.instructions.length}</Table.Cell>
                          <Table.Cell>{topic.conversationsCount}</Table.Cell>
                          <Table.Cell>
                            <Badge size="2" color="green">
                              {topic.goodFeedbackCount}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge size="2" color="red">
                              {topic.badFeedbackCount}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            {topic.brief && (
                              <Badge size="2" color="blue">
                                Has Brief
                              </Badge>
                            )}
                          </Table.Cell>
                        </Table.Row>
                        {expandedTopics.includes(topic.topicName) && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={6}
                              className={styles.expandedContent}
                            >
                              <Table.Root>
                                <Table.Body>
                                  {/* Brief Section */}
                                  <Table.Row className={styles.subRow}>
                                    <Table.Cell colSpan={2}>
                                      <Flex
                                        direction="column"
                                        gap="2"
                                        className={styles.sectionContainer}
                                      >
                                        <Flex align="center" gap="2">
                                          <Text weight="bold">Brief</Text>
                                        </Flex>
                                        <div className={styles.briefContent}>
                                          <Text
                                            size="2"
                                            style={{
                                              color: topic.brief
                                                ? "var(--gray-12)"
                                                : "var(--gray-9)",
                                            }}
                                          >
                                            {topic.brief
                                              ? <ReadOnlyMarkdown content={topic.brief} />
                                              : "No brief has been added"}
                                          </Text>
                                        </div>
                                      </Flex>
                                    </Table.Cell>
                                  </Table.Row>

                                  {/* Instructions Section */}
                                  <Table.Row className={styles.subRow}>
                                    <Table.Cell colSpan={2}>
                                      <Flex
                                        direction="column"
                                        gap="2"
                                        className={styles.sectionContainer}
                                      >
                                        <Flex align="center" gap="2">
                                          <Text weight="bold">
                                            Instructions
                                          </Text>
                                        </Flex>
                                        <div className={styles.sectionContent}>
                                          {topic.instructions.length > 0 ? (
                                            <Flex direction="column" gap="2">
                                              {topic.instructions.map(
                                                (instruction, idx) => (
                                                  <div
                                                    key={idx}
                                                    className={
                                                      styles.instruction
                                                    }
                                                  >
                                                    <Text size="2">
                                                      {instruction.content}
                                                    </Text>
                                                  </div>
                                                )
                                              )}
                                            </Flex>
                                          ) : (
                                            <Text size="2" color="gray">
                                              No instructions have been added
                                            </Text>
                                          )}
                                        </div>
                                      </Flex>
                                    </Table.Cell>
                                  </Table.Row>

                                  {/* Knowledge Source Section */}
                                  <Table.Row className={styles.subRow}>
                                    <Table.Cell colSpan={2}>
                                      <Flex
                                        direction="column"
                                        gap="2"
                                        className={styles.sectionContainer}
                                      >
                                        <Text weight="bold">
                                          Knowledge Sources
                                        </Text>
                                        <Flex direction="column" gap="2">
                                          <div
                                            className={
                                              styles.knowledgeSourceItem
                                            }
                                          >
                                            <Flex align="center" gap="2">
                                              <Text
                                                size="2"
                                                style={{
                                                  color: "var(--gray-12)",
                                                }}
                                              >
                                                Review help center articles:
                                              </Text>
                                              <Badge
                                                size="1"
                                                variant="soft"
                                                color={
                                                  topic.isKnowledgeEnabled
                                                    ? "green"
                                                    : "gray"
                                                }
                                              >
                                                {topic.isKnowledgeEnabled
                                                  ? "Enabled"
                                                  : "Disabled"}
                                              </Badge>
                                            </Flex>
                                          </div>
                                          <div
                                            className={
                                              styles.knowledgeSourceItem
                                            }
                                          >
                                            <Flex align="center" gap="2">
                                              <Text
                                                size="2"
                                                style={{
                                                  color: "var(--gray-12)",
                                                }}
                                              >
                                                Review historical tickets:
                                              </Text>
                                              <Badge
                                                size="1"
                                                variant="soft"
                                                color={
                                                  topic.isHistoricalTicketsEnabled
                                                    ? "green"
                                                    : "gray"
                                                }
                                              >
                                                {topic.isHistoricalTicketsEnabled
                                                  ? "Enabled"
                                                  : "Disabled"}
                                              </Badge>
                                            </Flex>
                                          </div>
                                        </Flex>
                                      </Flex>
                                    </Table.Cell>
                                  </Table.Row>

                                  <Table.Row className={styles.subRow}>
                                    <Table.Cell colSpan={2}>
                                      <Flex
                                        direction="column"
                                        gap="2"
                                        className={styles.sectionContainer}
                                      >
                                        <Flex
                                          justify="start"
                                          align="center"
                                          gap="2"
                                        >
                                          <Text weight="bold">
                                            Feedback Summary
                                          </Text>
                                          <Button
                                            color="gray"
                                            variant="outline"
                                            size="2"
                                            onClick={() =>
                                              navigate(
                                                buildActivityUrl(
                                                  version.simulatorId,
                                                  version.id
                                                )
                                              )
                                            }
                                          >
                                            See All Activity
                                          </Button>
                                        </Flex>
                                        <div className={styles.sectionContent}>
                                          {topic.conversationsCount > 0 ? (
                                            <Flex direction="column" gap="3">
                                              <Flex gap="2">
                                                <Badge
                                                  size="1"
                                                  variant="soft"
                                                  color="green"
                                                >
                                                  {topic.goodFeedbackCount} good
                                                </Badge>
                                                <Badge
                                                  size="1"
                                                  variant="soft"
                                                  color="red"
                                                >
                                                  {topic.badFeedbackCount} bad
                                                </Badge>
                                              </Flex>
                                              <Button
                                                color="gray"
                                                variant="outline"
                                                size="1"
                                                onClick={() =>
                                                  navigate(
                                                    buildActivityUrl(
                                                      version.simulatorId,
                                                      version.id,
                                                      topic.topicName
                                                    )
                                                  )
                                                }
                                              >
                                                See Topic Activity
                                              </Button>
                                            </Flex>
                                          ) : (
                                            <Flex
                                              align="start"
                                              direction="column"
                                              gap="2"
                                              py="4"
                                            >
                                              <Text size="2" color="gray">
                                                No sessions yet
                                              </Text>
                                            </Flex>
                                          )}
                                        </div>
                                      </Flex>
                                    </Table.Cell>
                                  </Table.Row>
                                </Table.Body>
                              </Table.Root>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </Table.Body>
            </Table.Root>
          </div>
        </div>
      </Flex>
    </div>
  );
};
